"use client";

import Link from "next/link";
import NextImage from "next/image";
import { motion } from "framer-motion";
import { HiArrowRight, HiGlobeAlt } from "react-icons/hi";
import { cn } from "@/lib/utils";
import { useNavbar } from "@/hooks/use-navbar";
import { useLanguage } from "@/lib/i18n";
import FullScreenMenu from "./FullScreenMenu";

export default function Navbar() {
  const { isMenuOpen, setIsMenuOpen, navClasses, scrolled } = useNavbar();
  const { language, setLanguage } = useLanguage();

  const toggleLanguage = () => {
    setLanguage(language === "id" ? "en" : "id");
  };

  const logoSrc = (scrolled || isMenuOpen)
    ? "/layout/horizontal-logo-dark.svg" // White Bg -> Dark Logo
    : "/layout/horizontal-logo-light.svg"; // Dark/Transparent Bg -> Light Logo

  return (
    <>
      <header className={navClasses.container}>
        {/* Container Utama - Menggunakan class container yang sudah di-update di globals.css */}
        <div className="container px-6 md:px-12 flex items-center justify-between h-full relative">
          {/* KIRI: Toggle Button + Language Switcher (Desktop) */}
          <div className="flex items-center justify-start z-30 min-w-fit gap-1 md:gap-3">
            <MenuToggle
              toggle={() => setIsMenuOpen(!isMenuOpen)}
              isOpen={isMenuOpen}
              className={navClasses.text}
            />
            
            {/* Language Switcher - Desktop Only */}
            <button
              onClick={toggleLanguage}
              className={cn(
                "hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-full transition-all duration-300 text-xs font-medium tracking-wider uppercase",
                scrolled || isMenuOpen
                  ? "text-brand-gold hover:bg-brand-gold/10 border border-brand-gold/30"
                  : "text-white/90 hover:bg-white/10 border border-white/30",
              )}
              aria-label="Toggle Language"
            >
              <HiGlobeAlt className="w-3.5 h-3.5" />
              <span className="font-sans">{language.toUpperCase()}</span>
            </button>
          </div>

          {/* TENGAH: Logo (HIDDEN on mobile, VISIBLE on md+) */}
          <div className="hidden md:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 text-center">
            <Link
              href={`/${language}`}
              onClick={() => setIsMenuOpen(false)}
              className="inline-block group"
            >
              <h1 className="flex flex-col items-center">
                <span className={cn(
                  "font-serif text-3xl md:text-4xl tracking-[0.1em] font-bold leading-none",
                  navClasses.logoColor
                )}>
                  KINKEN
                </span>
                <span className={cn(
                  "font-sans text-[0.6rem] md:text-xs uppercase tracking-[0.3em] font-medium mt-1.5",
                  navClasses.logoColor
                )}>
                  Luxury Living Since 2026
                </span>
              </h1>
            </Link>
          </div>

          {/* TENGAH MOBILE: Logo Compact */}
          <div className="md:hidden flex-1 flex justify-center">
            <Link
              href={`/${language}`}
              onClick={() => setIsMenuOpen(false)}
              className="inline-block group"
            >
              <h1 className="flex flex-col items-center">
                <span className={cn(
                  "font-serif text-2xl tracking-[0.1em] font-bold leading-none",
                  navClasses.logoColor
                )}>
                  KINKEN
                </span>
                <span className={cn(
                  "font-sans text-[0.5rem] uppercase tracking-[0.2em] font-medium mt-1",
                  navClasses.logoColor
                )}>
                  Luxury Living Since 2026
                </span>
              </h1>
            </Link>
          </div>
          
          {/* KANAN: Button */}
          <div className="flex items-center justify-end z-30 min-w-fit">
            <Link href={`/${language}/contact`} onClick={() => setIsMenuOpen(false)}>
              <div
                className={cn(
                  "group inline-flex items-center gap-2 md:gap-3 border font-light px-3 py-1.5 md:px-8 md:py-3 text-[10px] md:text-sm tracking-widest uppercase transition-all duration-500 backdrop-blur-sm cursor-pointer whitespace-nowrap",
                  scrolled || isMenuOpen
                    ? "border-brand-gold text-black hover:border-brand-gold hover:bg-brand-gold/10"
                    : "border-white/50 text-white hover:border-white hover:bg-white/5",
                )}
              >
                <span className="hidden sm:inline">{language === "id" ? "Pesan Sekarang" : "Book Now"}</span>
                <span className="sm:hidden">{language === "id" ? "Pesan" : "Book"}</span>
                <HiArrowRight
                  className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform duration-300"
                />
              </div>
            </Link>
          </div>
        </div>
      </header>

      <FullScreenMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
      />
    </>
  );
}

const MenuToggle = ({
  toggle,
  isOpen,
  className,
}: {
  toggle: () => void;
  isOpen: boolean;
  className?: string;
}) => (
  <button
    onClick={toggle}
    className={cn(
      "outline-none p-1 md:p-2 hover:opacity-70 z-[100] relative",
      className
    )}
    aria-label="Toggle Menu"
  >
    <svg width="23" height="23" viewBox="0 0 23 23" className="w-6 h-6 md:w-8 md:h-8">
      <Path
        variants={{
          closed: { d: "M 2 2.5 L 20 2.5" },
          open: { d: "M 3 16.5 L 17 2.5" },
        }}
        animate={isOpen ? "open" : "closed"}
      />
      <Path
        d="M 2 9.423 L 20 9.423"
        variants={{
          closed: { opacity: 1 },
          open: { opacity: 0 },
        }}
        transition={{ duration: 0.1 }}
        animate={isOpen ? "open" : "closed"}
      />
      <Path
        variants={{
          closed: { d: "M 2 16.346 L 20 16.346" },
          open: { d: "M 3 2.5 L 17 16.346" },
        }}
        animate={isOpen ? "open" : "closed"}
      />
    </svg>
  </button>
);

const Path = (props: any) => (
  <motion.path
    fill="transparent"
    strokeWidth="2.5"
    stroke="currentColor"
    strokeLinecap="round"
    {...props}
  />
);
